import { useState } from "react";
import {
  Loader2,
  Trash2,
  FolderOpen,
  Calendar,
  FileText,
  AlertTriangle,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { trpc } from "@/lib/trpc";
import type { DiscoveryStatus } from "@shared/discovery-types";
import { cn } from "@/lib/utils";

interface DiscoverySessionsListProps {
  onSelectSession: (
    sessionId: number,
    projectName: string,
    status: DiscoveryStatus
  ) => void;
}

const statusColors: Record<DiscoveryStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  uploading: "bg-info/10 text-info",
  processing: "bg-warning/10 text-warning",
  ready: "bg-success/10 text-success",
  committed: "bg-primary/10 text-primary",
  failed: "bg-destructive/10 text-destructive",
};

const statusLabels: Record<DiscoveryStatus, string> = {
  pending: "Draft",
  uploading: "Uploading",
  processing: "Processing",
  ready: "Ready to Import",
  committed: "Imported",
  failed: "Failed",
};

export function DiscoverySessionsList({
  onSelectSession,
}: DiscoverySessionsListProps) {
  const [deleteSessionId, setDeleteSessionId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showBatchDeleteDialog, setShowBatchDeleteDialog] = useState(false);

  const utils = trpc.useUtils();

  // Get all non-committed sessions
  const { data: sessions, isLoading } =
    trpc.companyDiscovery.listDiscoverySessions.useQuery(
      { limit: 50 },
      { refetchOnWindowFocus: false }
    );

  const deleteSession =
    trpc.companyDiscovery.deleteDiscoverySession.useMutation({
      onSuccess: () => {
        utils.companyDiscovery.listDiscoverySessions.invalidate();
        setDeleteSessionId(null);
      },
    });

  const batchDelete =
    trpc.companyDiscovery.batchDeleteDiscoverySessions.useMutation({
      onSuccess: () => {
        utils.companyDiscovery.listDiscoverySessions.invalidate();
        setSelectedIds(new Set());
        setShowBatchDeleteDialog(false);
      },
    });

  // Filter out committed sessions - those are already companies
  const draftSessions = sessions?.filter(s => s.status !== "committed") || [];

  const toggleSelect = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === draftSessions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(draftSessions.map(s => s.id)));
    }
  };

  const handleBatchDelete = () => {
    if (selectedIds.size > 0) {
      batchDelete.mutate({ sessionIds: Array.from(selectedIds) });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-40 animate-shimmer" />
          <Skeleton className="h-8 w-24 animate-shimmer" />
        </div>
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-5 w-5 animate-shimmer" />
                    <Skeleton className="h-10 w-10 rounded-lg animate-shimmer" />
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-40 animate-shimmer" />
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-3 w-24 animate-shimmer" />
                        <Skeleton className="h-3 w-16 animate-shimmer" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-6 w-20 rounded-full animate-shimmer" />
                    <Skeleton className="h-8 w-8 animate-shimmer" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (draftSessions.length === 0) {
    return null;
  }

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return `Today at ${date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    } else if (diffDays === 1) {
      return "Yesterday";
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const sessionToDelete = draftSessions.find(s => s.id === deleteSessionId);
  const allSelected =
    selectedIds.size === draftSessions.length && draftSessions.length > 0;
  const someSelected =
    selectedIds.size > 0 && selectedIds.size < draftSessions.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-muted-foreground">
          Continue a Draft ({draftSessions.length})
        </h3>

        <div className="flex items-center gap-2">
          {/* Select All / None Toggle */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-xs"
            onClick={toggleSelectAll}
          >
            {allSelected ? (
              <CheckSquare className="h-4 w-4 mr-1" />
            ) : someSelected ? (
              <MinusSquare className="h-4 w-4 mr-1" />
            ) : (
              <Square className="h-4 w-4 mr-1" />
            )}
            {allSelected ? "Deselect All" : "Select All"}
          </Button>

          {/* Batch Delete Button */}
          {selectedIds.size > 0 && (
            <Button
              variant="destructive"
              size="sm"
              className="h-8 text-xs"
              onClick={() => setShowBatchDeleteDialog(true)}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete {selectedIds.size} Selected
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {draftSessions.map(session => (
          <Card
            key={session.id}
            className={cn(
              "cursor-pointer transition-colors hover:bg-muted/50",
              session.status === "failed" && "border-destructive/50",
              selectedIds.has(session.id) && "ring-2 ring-primary ring-offset-2"
            )}
            onClick={() =>
              onSelectSession(
                session.id,
                session.projectName,
                session.status as DiscoveryStatus
              )
            }
          >
            <CardContent className="p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {/* Checkbox */}
                  <button
                    type="button"
                    className="shrink-0 p-1 rounded hover:bg-muted"
                    onClick={e => toggleSelect(session.id, e)}
                  >
                    {selectedIds.has(session.id) ? (
                      <CheckSquare className="h-5 w-5 text-primary" />
                    ) : (
                      <Square className="h-5 w-5 text-muted-foreground" />
                    )}
                  </button>

                  <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                    <FolderOpen className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {session.projectName}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(session.updatedAt || session.createdAt)}
                      </span>
                      {session.documentCount > 0 && (
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {session.documentCount} file
                          {session.documentCount !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      statusColors[session.status as DiscoveryStatus]
                    )}
                  >
                    {statusLabels[session.status as DiscoveryStatus]}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-destructive"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteSessionId(session.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Single Delete Confirmation Dialog */}
      <AlertDialog
        open={deleteSessionId !== null}
        onOpenChange={() => setDeleteSessionId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete Discovery Session?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{sessionToDelete?.projectName}" and
              all uploaded documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (deleteSessionId) {
                  deleteSession.mutate({ sessionId: deleteSessionId });
                }
              }}
              disabled={deleteSession.isPending}
            >
              {deleteSession.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Session"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Batch Delete Confirmation Dialog */}
      <AlertDialog
        open={showBatchDeleteDialog}
        onOpenChange={setShowBatchDeleteDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Delete {selectedIds.size} Sessions?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {selectedIds.size} discovery session
              {selectedIds.size !== 1 ? "s" : ""} and all their uploaded
              documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleBatchDelete}
              disabled={batchDelete.isPending}
            >
              {batchDelete.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting {selectedIds.size}...
                </>
              ) : (
                `Delete ${selectedIds.size} Sessions`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
